import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output, TemplateRef } from '@angular/core';

import { SportModel } from '@frontend/sports/betting-offer/feature/model';
import { GridConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { Column, GridSorting } from '@frontend/sports/grid/core/feature/model';
import { GridTrackingService, MarketSwitchSelector } from '@frontend/sports/grid/core/feature/tracking';
import { TooltipService } from '@frontend/sports/tooltip-feature';
import { LocalStoreService } from '@frontend/vanilla/core';
import { findIndex } from 'lodash-es';

import { TabBarItem } from '../../tab-bar/tab-bar.models';
import { HeaderGroup } from './grid-header.component';

@Component({
    selector: 'ms-grid-pills-header',
    templateUrl: 'grid-pills-header.component.html',
    styleUrls: ['./grid-pills-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridPillsHeaderComponent implements OnInit, OnChanges {
    isAccordionCollapsed = false;
    @Input() column: Column;
    @Input() sport: SportModel;
    @Input() hideTitle = false;
    @Input() useColoredIcon = false;
    @Input() tracking: string;
    @Input() sorting?: GridSorting;
    @Input() sortingTracking?: string;
    @Input() iconTemplate?: TemplateRef<any>;
    @Input() gridHeaderOverride?: string;

    @Output() sortingChange = new EventEmitter<GridSorting>();
    @Output() pillChange = new EventEmitter<HeaderGroup>();
    @Output() toggleAccordion = new EventEmitter<boolean>();

    private readonly onboardingTooltipKey = 'GridMarketSwitcherPillsOnboarding';
    pills: TabBarItem<string>[];
    showOnboardingTooltip = false;
    iconClass = '';

    @HostBinding('class.grid-header') gridHeaderClass = true;

    constructor(
        private gridConfig: GridConfig,
        private localStorage: LocalStoreService,
        public sitecore: Sitecore,
        private gridTrackingService: GridTrackingService,
        private tooltipService: TooltipService,
    ) {}

    ngOnInit(): void {
        if (this.gridConfig.showMarketPillsOnboardingMessage && !this.localStorage.get(this.onboardingTooltipKey)) {
            this.showOnboardingTooltip = true;
            this.localStorage.set(this.onboardingTooltipKey, true);
        }
    }

    ngOnChanges(): void {
        const marketGroups = this.column.groups.filter((group) => group.visible);
        this.iconClass = `sports-${this.sport.id}`;
        this.pills = marketGroups.map((group) => {
            return {
                id: group.id,
                title: group.name,
                active: group.active,
            };
        });
    }

    get active(): TabBarItem<string> | undefined {
        return this.pills?.filter((tab) => tab.active).pop();
    }

    pillSelected(pill: TabBarItem<string>): void {
        this.pills = this.pills.map((item) => ({
            ...item,
            active: item.id === pill.id,
        }));
        const marketId = pill.id;
        this.gridTrackingService.trackMarketSwitch({
            trackingLocation: this.tracking,
            marketName: pill.title,
            marketId,
            marketPosition: findIndex(this.column.groups, (group) => group.id === marketId),
            marketSwitchSelector: MarketSwitchSelector.Pill,
        });

        this.pillChange.next({
            column: this.column,
            group: this.column.groups.find((group) => group.id === marketId)!,
        });
    }

    sortingChanged(sortBy: GridSorting): void {
        this.sortingChange.next(sortBy);
    }

    closeTooltip(): void {
        this.tooltipService.hide(true);
    }

    headerClick(): void {
        this.isAccordionCollapsed = !this.isAccordionCollapsed;
        this.toggleAccordion.emit(this.isAccordionCollapsed);
    }
}
